import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { paymentsApi } from './api'
import type { PayoutAccountPayload, ProcessPayoutPayload, RefundPayload } from './types'

// Tenant dashboard
export function usePaymentsDashboard() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'payments', 'dashboard'),
    queryFn: () => paymentsApi.getDashboard(),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

// Drill-down items inside a historical payout.
export function usePayoutItems(payoutId: number | null | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'payments', 'payout-items', payoutId ?? null),
    queryFn: () => paymentsApi.getPayoutItems(Number(payoutId)),
    enabled: !!payoutId,
    staleTime: 0,
  })
}

export function usePayoutAccount() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'payments', 'payout-account'),
    queryFn: () => paymentsApi.getPayoutAccount(),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useSetPayoutAccount() {
  const tenantId = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: PayoutAccountPayload) => paymentsApi.setPayoutAccount(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'payments') })
    },
  })
}

// Issue a Stripe-automated refund and refresh the dashboard data.
export function useIssueRefund() {
  const tenantId = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RefundPayload) => paymentsApi.issueRefund(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'payments') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoices') })
      qc.invalidateQueries({ queryKey: ['admin', 'managed-payouts'] })
    },
  })
}

// Embedded Connect Components account session.
export function useCreateAccountSession(enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'payments', 'connect-session'),
    queryFn: () => paymentsApi.createAccountSession(),
    enabled,
    // The session is good for ~5 min; fetch on demand and let the
    // embedded components manage their own renewal.
    staleTime: 4 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

// ── Platform-admin ────────────────────────────────────────────────────

export function useAdminPayouts() {
  return useQuery({
    queryKey: ['admin', 'managed-payouts'],
    queryFn: () => paymentsApi.adminListPayouts(),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useAdminTenantPayouts(tenantId: string | null) {
  return useQuery({
    queryKey: ['admin', 'managed-payouts', tenantId],
    queryFn: () => paymentsApi.adminGetTenantPayouts(String(tenantId)),
    enabled: !!tenantId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useAdminRevenuePayments(
  currency: string | null,
  tenantId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['admin', 'managed-payouts', 'revenue-payments', currency, tenantId ?? null],
    queryFn: () => paymentsApi.adminGetRevenuePayments({
      currency: String(currency),
      tenantId: tenantId ?? null,
    }),
    enabled: enabled && !!currency,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useAdminProcessPayout(tenantId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ProcessPayoutPayload) =>
      paymentsApi.adminProcessPayout(String(tenantId), payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'managed-payouts'] })
      if (tenantId) qc.invalidateQueries({ queryKey: ['admin', 'managed-payouts', tenantId] })
    },
  })
}

export function useAdminUpdateFee(tenantId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (bokapsysFeeBps: number) =>
      paymentsApi.adminUpdateFee(String(tenantId), bokapsysFeeBps),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'managed-payouts'] })
      if (tenantId) qc.invalidateQueries({ queryKey: ['admin', 'managed-payouts', tenantId] })
    },
  })
}
